/**
 * Alpine components for the call surfaces:
 *   - incomingCallModal: the global ring modal (Accept / Decline).
 *   - inCallOverlay: the full-screen in-call surface (audio card or video).
 *   - threadCallButtons: the PM thread-header call affordance (button matrix).
 *
 * All three subscribe to the single call-state machine and call its methods.
 * Track attachment lives here because the overlay owns the <video>/<audio> refs.
 */

import { callState } from './call-state.js';
import { callApi, CallApiError } from './call-api.js';
import { followWebPhoneGateRefusal } from '../../modules/phone-gate-refusal.js';

const CALL_ACTIVE_STATUSES = ['ringing-out', 'connecting', 'active'];

function t(key, params) {
    return window.t ? window.t(key, params) : key;
}

function lucide(el) {
    if (window.LucideIcons) {
        window.LucideIcons.initialize(el);
    }
}

export function incomingCallModal() {
    return {
        visible: false,
        callId: '',
        type: 'audio',
        otherName: '',
        otherUsername: '',
        otherAvatar: '',
        _unsub: null,

        init() {
            this._unsub = callState.subscribe((s) => this.render(s));
            this.render(callState.snapshot());
        },

        destroy() {
            if (this._unsub) {
                this._unsub();
            }
        },

        render(s) {
            if (s.status === 'ringing-in') {
                this.callId = s.callId || '';
                this.type = s.type || 'audio';
                this.otherName = s.other?.name || s.other?.username || '';
                this.otherUsername = s.other?.username || '';
                this.otherAvatar = s.other?.avatar || '';
                if (!this.visible) {
                    this.visible = true;
                    this.$nextTick(() => lucide(this.$el));
                }
            } else if (this.visible) {
                this.visible = false;
            }
        },

        isVideo() {
            return this.type === 'video';
        },

        accept() {
            callState.accept();
        },

        decline() {
            callState.decline();
        },
    };
}

export function inCallOverlay() {
    return {
        visible: false,
        status: 'idle',
        type: 'audio',
        role: 'caller',
        otherId: '',
        otherName: '',
        otherUsername: '',
        otherAvatar: '',
        muted: false,
        cameraOff: false,
        hasRemoteVideo: false,
        hasLocalVideo: false,
        elapsed: 0,
        _timer: null,
        _startedAt: null,
        _unsub: null,
        _mediaUnsubs: [],
        _beforeUnload: null,

        init() {
            this._unsub = callState.subscribe((s) => this.render(s));
            this._mediaUnsubs.push(callState.on('remote-track', ({ track }) => this.attachRemote(track)));
            this._mediaUnsubs.push(callState.on('local-video-track', ({ track }) => this.attachLocal(track)));
            this._mediaUnsubs.push(callState.on('track-removed', ({ track }) => this.detach(track)));
            this.render(callState.snapshot());
        },

        destroy() {
            if (this._unsub) {
                this._unsub();
            }
            this._mediaUnsubs.forEach((fn) => fn && fn());
            this.stopTimer();
            this.removeUnloadGuard();
        },

        render(s) {
            const active = CALL_ACTIVE_STATUSES.includes(s.status);
            this.status = s.status;
            this.type = s.type || 'audio';
            this.role = s.role || 'caller';
            this.muted = s.muted;
            this.cameraOff = s.cameraOff;
            if (s.other) {
                this.otherId = s.other.id || '';
                this.otherName = s.other.name || s.other.username || '';
                this.otherUsername = s.other.username || '';
                this.otherAvatar = s.other.avatar || '';
            }

            if (active && !this.visible) {
                this.visible = true;
                this.addUnloadGuard();
            } else if (!active && this.visible) {
                this.visible = false;
                this.removeUnloadGuard();
                this.hasRemoteVideo = false;
                this.hasLocalVideo = false;
                this.elapsed = 0;
            }

            if (s.status === 'active' && !this._timer) {
                this.startTimer(s.startedAt);
            } else if (s.status !== 'active' && this._timer) {
                this.stopTimer();
            }

            if (active) {
                this.$nextTick(() => lucide(this.$el));
            }
        },

        // --- media attachment ---
        attachRemote(track) {
            if (!track) {
                return;
            }
            if (track.kind === 'video') {
                track.attach(this.$refs.remoteVideo);
                this.hasRemoteVideo = true;
            } else if (track.kind === 'audio') {
                track.attach(this.$refs.remoteAudio);
            }
        },

        attachLocal(track) {
            if (track && this.$refs.localVideo) {
                track.attach(this.$refs.localVideo);
                this.hasLocalVideo = true;
            }
        },

        detach(track) {
            if (!track) {
                return;
            }
            try {
                track.detach();
            } catch (e) {
                /* ignore */
            }
            if (track.kind === 'video') {
                this.hasRemoteVideo = false;
            }
        },

        // --- timer ---
        startTimer(startedAt) {
            this._startedAt = startedAt || Date.now();
            this.tick();
            this._timer = setInterval(() => this.tick(), 500);
        },

        tick() {
            this.elapsed = Math.max(0, Math.round((Date.now() - this._startedAt) / 1000));
        },

        stopTimer() {
            if (this._timer) {
                clearInterval(this._timer);
                this._timer = null;
            }
        },

        timerText() {
            const total = this.elapsed;
            const minutes = Math.floor(total / 60);
            const seconds = String(total % 60).padStart(2, '0');
            return `${minutes}:${seconds}`;
        },

        statusText() {
            if (this.status === 'active') {
                return this.timerText();
            }
            if (this.status === 'connecting') {
                return t('common.calls_connecting');
            }
            return t('common.calls_calling');
        },

        // --- unload guard ---
        addUnloadGuard() {
            if (this._beforeUnload) {
                return;
            }
            this._beforeUnload = (e) => {
                e.preventDefault();
                e.returnValue = '';
                return '';
            };
            window.addEventListener('beforeunload', this._beforeUnload);
        },

        removeUnloadGuard() {
            if (this._beforeUnload) {
                window.removeEventListener('beforeunload', this._beforeUnload);
                this._beforeUnload = null;
            }
        },

        // --- controls ---
        isVideo() {
            return this.type === 'video';
        },

        showRemoteVideoStage() {
            return this.isVideo() && this.status === 'active' && this.hasRemoteVideo;
        },

        toggleMute() {
            callState.toggleMute();
        },

        toggleCamera() {
            callState.toggleCamera();
        },

        switchCamera() {
            callState.switchCamera();
        },

        canSwitchCamera() {
            return this.isVideo() && callState.canSwitchCamera();
        },

        end() {
            callState.hangUp();
        },

        async reportAndEnd() {
            const id = this.otherId;
            callState.hangUp();

            // Reporting is the primary safety action, so open it first. A slow or
            // failing block must never delay or abort the report. Block and revoke
            // run best-effort afterward.
            if (id && window.openReportModal) {
                window.openReportModal('user', id, id);
            }

            if (id) {
                try {
                    await callApi.blockUser(id);
                } catch (e) {
                    window.showError?.(t('common.calls_error_unavailable'));
                }

                try {
                    await callApi.revokeAuthorization(id);
                } catch (e) {
                    // A block is already enough to prevent future calls. Revoke is
                    // best-effort cleanup for the pair authorization row.
                }
            }
        },
    };
}

export function threadCallButtons() {
    return {
        active: false,
        uid: '',
        username: '',
        name: '',
        avatar: '',
        selfUid: '',
        loading: true,
        callsEnabled: false,
        canCall: false,
        canRequest: false,
        authStatus: 'none',
        requestedBy: null,
        busy: false,
        _unsubs: [],
        _loadSeq: 0,

        init() {
            this.selfUid = document.querySelector('meta[name="user-id"]')?.content || '';

            const onOpen = (e) => this.setContact(e.detail || {});
            const onClose = () => this.clearContact();
            const onAuth = (e) => {
                const from = e.detail && e.detail.userId ? String(e.detail.userId) : null;
                if (this.active && (!from || from === this.uid)) {
                    this.load();
                }
            };
            const onChanged = (e) => {
                const uid = e.detail && e.detail.userId ? String(e.detail.userId) : null;
                if (this.active && (!uid || uid === this.uid)) {
                    this.load();
                }
            };
            document.addEventListener('pm:thread-opened', onOpen);
            document.addEventListener('pm:thread-closed', onClose);
            document.addEventListener('ws:call-auth-accepted', onAuth);
            document.addEventListener('ws:call-auth-request', onAuth);
            document.addEventListener('calls:authorization-changed', onChanged);
            this._unsubs.push(() => document.removeEventListener('pm:thread-opened', onOpen));
            this._unsubs.push(() => document.removeEventListener('pm:thread-closed', onClose));
            this._unsubs.push(() => document.removeEventListener('ws:call-auth-accepted', onAuth));
            this._unsubs.push(() => document.removeEventListener('ws:call-auth-request', onAuth));
            this._unsubs.push(() => document.removeEventListener('calls:authorization-changed', onChanged));

            // A thread can already be open on initial page load (deep link to
            // /app/messages?thread_id=...), where pm:thread-opened has not fired.
            this.detectActiveThread();
        },

        detectActiveThread() {
            const row = document.querySelector('#threads_left .pm_thread.active');
            if (row) {
                this.setContact({
                    uid: row.getAttribute('data-uid') || '',
                    username: row.getAttribute('data-username') || '',
                    name: row.getAttribute('data-name') || '',
                    avatar: row.getAttribute('data-thumb') || '',
                    deleted: row.getAttribute('data-contact-badge-status') === 'deleted',
                });
            }
        },

        destroy() {
            this._unsubs.forEach((fn) => fn && fn());
        },

        // No contact to call. Reset every per-contact field matrix() reads, so
        // it cannot report the previous contact as callable. selfUid is the
        // viewer's own id and stays.
        clearContact() {
            // A load in flight belongs to the contact being dropped, so retire
            // its sequence. Otherwise its reply settles onto a cleared component.
            this._loadSeq++;
            this.uid = '';
            this.active = false;
            this.loading = false;
            this.callsEnabled = false;
            this.canCall = false;
            this.canRequest = false;
            this.authStatus = 'none';
            this.requestedBy = null;
            this.syncRevokeItem();
        },

        // A deleted contact has no user row, so GET /v1/call-authorization
        // answers 404 for them. Treat them as no contact and never ask.
        setContact({ uid, username, name, avatar, deleted }) {
            const nextUid = deleted ? '' : String(uid || '');
            if (!nextUid) {
                this.clearContact();
                return;
            }
            // A deep link opens one contact twice, through detectActiveThread()
            // and pm:thread-opened. Suppress only while in flight: a later open
            // of the same contact must still refresh, a failed load included.
            if (this.active && this.uid === nextUid && this.loading) {
                // The second open carries the fresher metadata, so take it.
                this.username = username || '';
                this.name = name || username || '';
                this.avatar = avatar || '';
                return;
            }
            this.uid = nextUid;
            this.username = username || '';
            this.name = name || username || '';
            this.avatar = avatar || '';
            this.active = true;
            this.loading = true;
            this.callsEnabled = false;
            this.canCall = false;
            this.canRequest = false;
            this.authStatus = 'none';
            this.requestedBy = null;
            this.syncRevokeItem();
            this.load();
        },

        async load() {
            const uid = this.uid;
            if (!uid) {
                this.loading = false;
                return;
            }
            // Open A, open B, open A again leaves two loads in flight for A.
            // The uid matches on both, so only the sequence separates them.
            const request = ++this._loadSeq;
            this.loading = true;
            try {
                const info = await callApi.getAuthorization(uid);
                if (request !== this._loadSeq) {
                    return;
                }
                this.callsEnabled = info.calls_enabled === true;
                this.canCall = !!info.can_call;
                this.canRequest = !!info.can_request_authorization;
                this.authStatus = info.authorization?.status || 'none';
                this.requestedBy = info.authorization?.requested_by
                    ? String(info.authorization.requested_by)
                    : null;
            } catch (e) {
                if (request !== this._loadSeq) {
                    return;
                }
                this.callsEnabled = false;
                this.canCall = false;
                this.canRequest = false;
                this.authStatus = 'none';
                this.requestedBy = null;
            } finally {
                if (request !== this._loadSeq) {
                    return;
                }
                this.loading = false;
                this.syncRevokeItem();
                this.$nextTick(() => lucide(this.$el));
            }
        },

        // The revoke control lives in the conversation-header options menu
        // (outside this component), so its visibility is synced imperatively to
        // the authorized state.
        syncRevokeItem() {
            const item = document.getElementById('pm-call-revoke-item');
            if (!item) {
                return;
            }
            const show = this.active && this.authStatus === 'authorized';
            item.classList.toggle('hidden', !show);
            item.classList.toggle('block', show);
        },

        /**
         * The button-matrix state:
         *   inbound  - the other member asked to call me (accept / decline)
         *   call     - fully callable (authorized + every gate passes)
         *   sent     - my request is pending
         *   request  - requestable pending authorization (offer to request)
         *   hidden   - nothing to show
         */
        matrix() {
            if (this.loading) {
                return 'loading';
            }
            if (!this.callsEnabled) {
                return 'hidden';
            }
            if (this.authStatus === 'requested' && this.requestedBy && this.requestedBy === this.uid) {
                return 'inbound';
            }
            if (this.canCall) {
                return 'call';
            }
            if (this.authStatus === 'requested' && this.requestedBy === this.selfUid) {
                return 'sent';
            }
            if (this.authStatus === 'authorized') {
                return 'hidden';
            }
            // can_call=false covers many denials a request cannot cure (the
            // other side not in the rollout, ACL, do-not-disturb, the age
            // band). Only offer "request to call" when the server says a
            // request would actually be accepted.
            return this.canRequest ? 'request' : 'hidden';
        },

        startAudio() {
            this.start('audio');
        },

        startVideo() {
            this.start('video');
        },

        async start(type) {
            if (this.busy || !window.InterpalsCalls) {
                return;
            }
            this.busy = true;
            try {
                await window.InterpalsCalls.startCall(this.uid, type, {
                    id: this.uid,
                    username: this.username,
                    name: this.name,
                    avatar: this.avatar,
                });
            } catch (error) {
                this.handleError(error);
            } finally {
                this.busy = false;
            }
        },

        async requestCall() {
            if (this.busy) {
                return;
            }
            this.busy = true;
            // Snapshot the contact: a thread switch during the awaits below
            // re-points this component, and the response state / toast name
            // must stay bound to the contact the user actually asked for.
            const uid = this.uid;
            const name = this.name;
            try {
                // Apply the state the server actually reached, not an assumed
                // 'requested': mutual interest (the other side had an open ask
                // to us) authorizes the pair on this POST.
                const response = await callApi.requestAuthorization(uid);
                const auth = response && response.authorization;
                const status = auth && auth.status;
                if (status === 'authorized') {
                    if (this.uid === uid) {
                        // can_call still needs the server verdict (ACL, DND, ...).
                        await this.load();
                    }
                    window.showSuccess?.(t('common.calls_toast_auth_accepted', { username: name }));
                } else if (status === 'revoked' || status === 'declined') {
                    // The pair sits in a state a stale view of this thread did
                    // not know about (e.g. a still-cooling withdrawal from
                    // another tab): the POST changed nothing, so claiming a
                    // request was sent would be false. Apply and re-fetch the
                    // affordances instead.
                    if (this.uid === uid) {
                        this.authStatus = status;
                        this.requestedBy = auth.requested_by ? String(auth.requested_by) : null;
                        await this.load();
                    }
                } else {
                    if (this.uid === uid) {
                        this.authStatus = status || 'requested';
                        this.requestedBy = (auth && auth.requested_by) || this.selfUid;
                    }
                    window.showSuccess?.(t('common.calls_toast_request_sent'));
                }
            } catch (error) {
                this.handleError(error);
            } finally {
                this.busy = false;
                this.$nextTick(() => lucide(this.$el));
            }
        },

        async acceptInbound() {
            if (this.busy) {
                return;
            }
            this.busy = true;
            try {
                await callApi.acceptAuthorization(this.uid);
                // Authorization is necessary but not sufficient for can_call
                // (ACL, DND, age band, rollout are all evaluated server-side),
                // so re-fetch instead of optimistically flipping to callable.
                await this.load();
            } catch (error) {
                this.handleError(error);
            } finally {
                this.busy = false;
                this.$nextTick(() => lucide(this.$el));
            }
        },

        async declineInbound() {
            if (this.busy) {
                return;
            }
            this.busy = true;
            try {
                await callApi.declineAuthorization(this.uid);
                this.authStatus = 'none';
                this.requestedBy = null;
            } catch (error) {
                this.handleError(error);
            } finally {
                this.busy = false;
                this.$nextTick(() => lucide(this.$el));
            }
        },

        handleError(error) {
            const code = error instanceof CallApiError ? error.code : '';

            // No calls_error_* copy names the phone gate, so the fallback below
            // would answer it with "unavailable" and no route. The verify page
            // is the route.
            if (followWebPhoneGateRefusal({ error: code })) return;

            const key = `common.calls_error_${code || 'unavailable'}`;
            const translated = t(key, { username: this.name || this.username });
            // Fall back to a generic message when a specific code has no copy.
            const message = translated === key
                ? t('common.calls_error_unavailable', { username: this.name || this.username })
                : translated;
            window.showError?.(message);

            // Codes that mean the affordance is now stale: re-fetch so the button
            // reflects the true state (authorization revoked, target busy, ...).
            if (code === 'call_authorization_required' || code === 'call_busy' || code === 'call_invalid_state') {
                this.load();
            }
        },
    };
}

export default { incomingCallModal, inCallOverlay, threadCallButtons };
